import { mediaUrl, useSiteMedia } from "../lib/siteMedia";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SectionHeader from "../components/SectionHeader";
import { api } from "../lib/api";

const FEATURED_BRANDS = [
  "Davidoff", "Padron", "Arturo Fuente", "Cohiba", "Montecristo", "Romeo y Julieta", "Oliva", "My Father", "Tatuaje",
];

export default function CigarLounge() {
  const media = useSiteMedia();
  const [cigars, setCigars] = useState([]);
  useEffect(() => { api.get("/products?category=cigars").then((r) => setCigars(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="cigar-lounge-page">
      <section className="relative pt-32 min-h-[80vh] flex items-end px-6 sm:px-10 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={mediaUrl(media, "cigar_hero")} alt="" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-[1600px] mx-auto pb-20 w-full">
          <p className="eyebrow">The Cigar Lounge</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
            A walk-in humidor. <span className="italic" style={{ color: "var(--twr-bronze)" }}>A library of smoke.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/80 text-lg mt-6 max-w-2xl">
            Hand-selected from Geneva to Estelí. Members keep private lockers. Staff who speak the language of the leaf.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/reservations?type=cigar-lounge" data-testid="lounge-reserve" className="btn-bronze">Reserve a Seat <ChevronRight size={14} /></Link>
            <Link to="/membership" className="btn-ghost">Private Locker</Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="eyebrow">The Humidor</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Climate. Curated.</h2>
            <p className="text-[var(--twr-warm-white)]/70 mt-6 leading-relaxed">
              Held at a steady 70°F and 70% humidity. Stocked across Dominican, Nicaraguan, Honduran, and rare Cuban-seed cigars.
              Released allocations on Thursdays — first to members, then to the floor.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10 max-w-md">
              <div className="stat"><span className="stat-value">600+</span><span className="stat-label">SKUs</span></div>
              <div className="stat"><span className="stat-value">14</span><span className="stat-label">Lockers</span></div>
              <div className="stat"><span className="stat-value">9</span><span className="stat-label">Featured Houses</span></div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 frame aspect-[16/10]">
            <img src={mediaUrl(media, "cigar_feature")} alt="Humidor" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="section-tight bg-[#0c0b0a] overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-0">
          <p className="eyebrow text-center">Featured Houses</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {FEATURED_BRANDS.map((b) => (
              <span key={b} className="font-serif text-2xl sm:text-3xl text-[var(--twr-warm-white)]/60 hover:text-[var(--twr-bronze)] transition-colors duration-300">{b}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <SectionHeader eyebrow="The Collection" title="From the shop — current allocations." />
            <Link to="/shop?category=cigars" className="link-bronze text-xs uppercase tracking-[0.28em]">Shop All Cigars</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {cigars.slice(0, 6).map((p) => (
              <Link key={p.id} to={`/shop/${p.id}`} data-testid={`lounge-cigar-${p.id}`} className="group product-card overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <p className="eyebrow">{p.origin || "Curated"} · {p.strength || ""}</p>
                  <h3 className="font-serif text-xl mt-3 leading-snug">{p.name}</h3>
                  <p className="text-[var(--twr-warm-white)]/65 text-sm mt-3 line-clamp-2">{p.description}</p>
                  <div className="flex items-center justify-between mt-5">
                    <span className="font-serif text-lg text-[var(--twr-bronze)]">${p.price.toFixed(2)}</span>
                    <span className="text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/55">View</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0c0b0a]">
        <div className="max-w-[1100px] mx-auto text-center">
          <p className="eyebrow">Pairings & Education</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">Sit longer. Learn deeper.</h2>
          <p className="text-[var(--twr-warm-white)]/70 mt-6 leading-relaxed">
            Monthly pairing dinners. Cigar 101 evenings. Founders-only master classes with visiting blenders from the great houses.
            Always small. Always seated.
          </p>
          <Link to="/events" className="btn-bronze mt-10">See Upcoming Tastings <ChevronRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
